import React, { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import {
  AlertCircle,
  ArrowLeft,
  ArrowRight,
  CalendarClock,
  ClipboardCheck,
  Eye,
  FileText,
  Flag,
  FlaskConical,
  Handshake,
  Link2,
  ListChecks,
  ShieldAlert,
  TrendingUp,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import AgreementsShell from "@/components/agreements/AgreementsShell";
import LegalDisclaimer, { AttorneyBadgeNote } from "@/components/agreements/LegalDisclaimer";
import CategoryIcon from "@/components/agreements/CategoryIcon";
import DocumentCard from "@/components/agreements/DocumentCard";
import { CATEGORY_BY_ID } from "@/data/agreements/categories";
import { IMPORTANCE_BY_ID, STAGE_BY_ID } from "@/data/agreements/stages";
import { DOC_BY_ID } from "@/data/agreements/documentLibrary";
import { RED_FLAG_BY_DOC_ID } from "@/data/agreements/redFlagChecklists";
import { hasTemplate } from "@/data/agreements/templateLibrary";
import { eventsApi } from "@/lib/agreements/api";

/**
 * DocumentDetail — route: /agreements/library/:docId
 *
 * The full library entry: when you need it, what it does, why it matters, the business terms,
 * the clauses to argue about, the CPG-specific traps, the mistakes, the asks, the red flags,
 * and what to have in hand before you sign.
 */
export default function DocumentDetail() {
  const { docId } = useParams();
  const doc = DOC_BY_ID[docId] || null;

  useEffect(() => {
    if (!doc) return;
    eventsApi.track({ type: "view_document", docId: doc.id, categoryId: doc.category });
  }, [doc]);

  if (!doc) {
    return (
      <AgreementsShell
        crumbs={[{ label: "Library", to: "/agreements/library" }, { label: "Not found" }]}
        title="We do not have that document"
        subtitle={`Nothing in the library uses the id "${docId}". It may have been renamed, or the link may be mistyped.`}
      >
        <div className="flex flex-wrap gap-2">
          <Button asChild>
            <Link to="/agreements/library">
              <ArrowLeft className="mr-2 h-4 w-4" /> Back to the library
            </Link>
          </Button>
          <Button asChild variant="outline">
            <Link to="/agreements/finder">Help me find the right one</Link>
          </Button>
        </div>
      </AgreementsShell>
    );
  }

  const category = CATEGORY_BY_ID[doc.category] || null;
  const importance = IMPORTANCE_BY_ID[doc.importance] || null;
  const redFlagList = RED_FLAG_BY_DOC_ID[doc.id] || null;
  const templateExists = hasTemplate(doc.id);
  const showAttorneyNote = doc.attorneyReview && doc.attorneyReview !== "depends";
  const aliases = (doc.aliases || []).filter(Boolean);
  const related = (doc.related || []).map((id) => DOC_BY_ID[id]).filter(Boolean);

  return (
    <AgreementsShell
      crumbs={[
        { label: "Library", to: "/agreements/library" },
        ...(category ? [{ label: category.short, to: `/agreements/library#cat-${category.id}` }] : []),
        { label: doc.name },
      ]}
      title={
        <span className="flex items-start gap-3">
          <CategoryIcon categoryId={doc.category} size="lg" />
          <span className="min-w-0">{doc.name}</span>
        </span>
      }
      subtitle={aliases.length > 0 ? `Also called: ${aliases.join(" · ")}` : undefined}
      actions={
        templateExists ? (
          <Button asChild size="lg" className="bg-indigo-600 hover:bg-indigo-700">
            <Link
              to={`/agreements/template/${doc.id}`}
              onClick={() => eventsApi.track({ type: "open_template", docId: doc.id, context: "document-detail" })}
            >
              <FileText className="mr-2 h-4 w-4" /> Open template
            </Link>
          </Button>
        ) : undefined
      }
    >
      {/* Who signs it, and where it sits */}
      <div className="mb-5 flex flex-wrap items-center gap-x-3 gap-y-2 text-sm">
        {doc.parties && (
          <span className="inline-flex items-center gap-1.5">
            <Handshake className="h-4 w-4 text-muted-foreground" />
            <span className="text-muted-foreground">Signed by</span>
            <span className="font-medium">{doc.parties}</span>
          </span>
        )}
        {importance && (
          <Badge variant="secondary" title={importance.blurb}>
            {importance.name}
          </Badge>
        )}
        {(doc.stages || []).map((stageId) => (
          <Badge key={stageId} variant="outline" className="font-normal">
            {STAGE_BY_ID[stageId]?.name || stageId}
          </Badge>
        ))}
        {doc.subcategory && (
          <span className="text-xs text-muted-foreground">{doc.subcategory}</span>
        )}
      </div>

      {showAttorneyNote && (
        <AttorneyBadgeNote
          level={doc.attorneyReview}
          note={
            doc.attorneyNote ||
            category?.attorneyNote ||
            "The terms in this document decide real money and are hard to undo once signed."
          }
          className="mb-5"
        />
      )}

      {/* Red-flag checklist + guidance-only note */}
      {(redFlagList || !templateExists) && (
        <div
          className={cn(
            "mb-8 grid gap-3",
            redFlagList && !templateExists && "sm:grid-cols-2",
          )}
        >
          {redFlagList && (
            <Link
              to={`/agreements/red-flags/${redFlagList.id}`}
              className="group rounded-xl focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <Card className="h-full border-red-200 bg-red-50 transition-transform group-hover:-translate-y-0.5 dark:border-red-900 dark:bg-red-950/30">
                <CardContent className="flex h-full items-start gap-3 p-4">
                  <ShieldAlert className="mt-0.5 h-5 w-5 shrink-0 text-red-600" />
                  <div className="min-w-0">
                    <h3 className="font-semibold text-red-900 dark:text-red-200">
                      Red-flag checklist for this agreement
                    </h3>
                    <p className="mt-1 text-sm text-red-800/90 dark:text-red-200/80">
                      Hold the draft in one hand and walk the list. Anything you cannot answer from
                      the document itself is the answer.
                    </p>
                    <span className="mt-2 inline-flex items-center gap-1 text-sm font-medium text-red-900 dark:text-red-200">
                      Open the checklist
                      <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                    </span>
                  </div>
                </CardContent>
              </Card>
            </Link>
          )}

          {!templateExists && (
            <Card className="h-full border-dashed">
              <CardContent className="flex h-full items-start gap-3 p-4">
                <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-muted-foreground" />
                <div className="min-w-0">
                  <h3 className="font-semibold">No template for this one — guidance only</h3>
                  <p className="mt-1 text-sm text-muted-foreground">
                    {category?.attorneyNote ||
                      "We ship a starting template only where a generic draft is more use than harm. This one turns on the specific deal, on the counterparty's own form, and on the law where the two of you sit — a fill-in-the-blanks version would hand you false confidence in a document that decides real money."}
                  </p>
                  <p className="mt-2 text-sm text-muted-foreground">
                    Use this entry to know what to look for, what to ask for and what should stop
                    your signature — then work the counterparty's draft, or your attorney's.
                  </p>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      <Section icon={CalendarClock} title="When you need it">
        <Prose>{doc.whenYouNeedIt}</Prose>
      </Section>

      <Section icon={FileText} title="What it does">
        <Prose>{doc.whatItDoes}</Prose>
      </Section>

      <Section icon={TrendingUp} title="Why it matters">
        <Prose>{doc.whyItMatters}</Prose>
      </Section>

      <Section icon={ListChecks} title="Major business terms" count={doc.majorTerms?.length}>
        <ul className="grid gap-2 sm:grid-cols-2">
          {(doc.majorTerms || []).map((term, i) => (
            <li
              key={i}
              className="flex items-start gap-2 rounded-md border bg-card px-3 py-2 text-sm"
            >
              <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-indigo-500" />
              {term}
            </li>
          ))}
        </ul>
      </Section>

      <Section
        icon={Eye}
        title="Clauses to pay attention to"
        count={doc.clausesToWatch?.length}
        note="What goes wrong when each of these is written against you."
      >
        <ul className="space-y-2.5">
          {(doc.clausesToWatch || []).map((item, i) => (
            <li key={i} className="rounded-lg border bg-card p-3.5 text-sm">
              <p className="font-semibold">{item.clause}</p>
              <p className="mt-1 text-muted-foreground">{item.why}</p>
            </li>
          ))}
        </ul>
      </Section>

      <Section
        icon={FlaskConical}
        title="CPG-specific issues"
        count={doc.cpgIssues?.length}
        note="The things a generic version of this contract would miss."
      >
        <BulletList items={doc.cpgIssues} dotClass="bg-violet-500" />
      </Section>

      <Section icon={AlertCircle} title="Common founder mistakes" count={doc.founderMistakes?.length}>
        <BulletList items={doc.founderMistakes} dotClass="bg-pink-500" />
      </Section>

      <Section
        icon={Handshake}
        title="Negotiation points"
        count={doc.negotiationPoints?.length}
        note="Phrased as the ask. Bring these to the table before you are emotionally committed."
      >
        <BulletList items={doc.negotiationPoints} dotClass="bg-emerald-500" />
      </Section>

      <Section icon={Flag} title="Red flags" count={doc.redFlags?.length}>
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 dark:border-red-900 dark:bg-red-950/30">
          <p className="mb-2.5 text-sm font-medium text-red-900 dark:text-red-200">
            Language that should stop a signature until it is resolved.
          </p>
          <ul className="space-y-2">
            {(doc.redFlags || []).map((flag, i) => (
              <li
                key={i}
                className="flex items-start gap-2 text-sm text-red-900 dark:text-red-200/90"
              >
                <Flag className="mt-0.5 h-3.5 w-3.5 shrink-0 text-red-600" />
                {flag}
              </li>
            ))}
          </ul>
          {redFlagList && (
            <Button asChild size="sm" variant="outline" className="mt-3 border-red-300 bg-white/70">
              <Link to={`/agreements/red-flags/${redFlagList.id}`}>
                Work the full red-flag checklist <ArrowRight className="ml-1.5 h-3.5 w-3.5" />
              </Link>
            </Button>
          )}
        </div>
      </Section>

      <Section icon={ClipboardCheck} title="Before you sign" count={doc.beforeYouSign?.length}>
        <ul className="space-y-2">
          {(doc.beforeYouSign || []).map((item, i) => (
            <li key={i} className="flex items-start gap-2.5 rounded-md border bg-card p-3 text-sm">
              <span className="mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded border text-[11px] font-semibold text-muted-foreground">
                {i + 1}
              </span>
              {item}
            </li>
          ))}
        </ul>
      </Section>

      {related.length > 0 && (
        <Section icon={Link2} title="Related agreements" count={related.length}>
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
            {related.map((rel) => (
              <DocumentCard key={rel.id} doc={rel} compact />
            ))}
          </div>
        </Section>
      )}

      <div className="mb-6 flex flex-wrap gap-2">
        {templateExists && (
          <Button asChild className="bg-indigo-600 hover:bg-indigo-700">
            <Link
              to={`/agreements/template/${doc.id}`}
              onClick={() => eventsApi.track({ type: "open_template", docId: doc.id, context: "document-detail-footer" })}
            >
              <FileText className="mr-2 h-4 w-4" /> Open template
            </Link>
          </Button>
        )}
        <Button asChild variant="outline">
          <Link to="/agreements/library">
            <ArrowLeft className="mr-2 h-4 w-4" /> Back to the library
          </Link>
        </Button>
      </div>

      <LegalDisclaimer variant="library" />
    </AgreementsShell>
  );
}

function Section({ icon: Icon, title, count, note, children }) {
  return (
    <section className="mb-8 scroll-mt-24">
      <div className="mb-3">
        <h2 className="flex flex-wrap items-center gap-2 text-lg font-bold tracking-tight sm:text-xl">
          <Icon className="h-5 w-5 shrink-0 text-muted-foreground" />
          {title}
          {typeof count === "number" && count > 0 && (
            <span className="text-xs font-medium tabular-nums text-muted-foreground">{count}</span>
          )}
        </h2>
        {note && <p className="mt-1 text-sm text-muted-foreground">{note}</p>}
      </div>
      {children}
    </section>
  );
}

function Prose({ children }) {
  if (!children) return <p className="text-sm text-muted-foreground">Not documented for this entry.</p>;
  return <p className="max-w-3xl text-[15px] leading-relaxed">{children}</p>;
}

function BulletList({ items, dotClass }) {
  const list = items || [];
  if (list.length === 0) {
    return <p className="text-sm text-muted-foreground">Not documented for this entry.</p>;
  }
  return (
    <ul className="space-y-2">
      {list.map((item, i) => (
        <li key={i} className="flex items-start gap-2.5 text-sm leading-relaxed">
          <span className={cn("mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full", dotClass)} />
          {item}
        </li>
      ))}
    </ul>
  );
}
